import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import useI18n from '@/shared/hooks/usei18n'
import { Button, GenericDialog } from '@/common'
import CbamSimulationGoodForm, {
  initialState
} from '../components/CbamSimulationGoodForm/CbamSimulationGoodForm'
import {
  CbamSimulationGoodFormData,
  validateCbamSimulationGoodForm
} from '../components/CbamSimulationGoodForm/CbamSimulationGoodForm.schema'
import { useGetAllSupplierEmissionsReportsForOrganization } from '@/features/suppliers/hooks/supplierEmissionsReport'
import { useSession } from '@/shared/hooks/useSession'
import { useCreateCbamSimulationGood } from '@/features/cbam-simulations-v2/hooks/cbamSimulationGoods'
import CbamSimulationGoodDataSection from '../components/CbamSimulationGoodForm/CbamSimulationGoodDataSection'
import { useGetAllInstallationsForOrganization } from '@/features/suppliers/hooks/installations'
import { useGetDefaultValues } from '@/shared/hooks/goods'
import { trackCbamSimulationSignal } from '@/features/cbam-simulations-v2/analytics/cbamSimulationClarity'

export default function SimulationGoodCreateDialog({
  isOpen,
  close,
  cbamSimulationId,
  simulationYear
}: {
  isOpen: boolean
  close: () => void
  cbamSimulationId: string
  simulationYear: number
}) {
  const { t } = useI18n()
  const organizationId = useSession().session.organization.id
  const supplierEmissionsReportsQuery =
    useGetAllSupplierEmissionsReportsForOrganization(organizationId)
  const { mutateAsync: createSimulationGood, isPending: isCreatingSimulationGood } =
    useCreateCbamSimulationGood(organizationId, cbamSimulationId)
  const installationsQuery = useGetAllInstallationsForOrganization(organizationId)

  const installations = installationsQuery.data ?? []

  const supplierEmissionsReports = useMemo(
    () => supplierEmissionsReportsQuery.data ?? [],
    [supplierEmissionsReportsQuery.data]
  )

  const [formData, setFormData] = useState<CbamSimulationGoodFormData>(initialState)

  const defaultValueQuery = useGetDefaultValues(formData.cnCode, formData.country, simulationYear)

  const defaultSpecificDirectEmbeddedEmissions = useMemo(() => {
    return defaultValueQuery.data?.directEmissions
      ? Number(defaultValueQuery.data.directEmissions)
      : 0
  }, [defaultValueQuery.data])

  const defaultSpecificIndirectEmbeddedEmissions = useMemo(() => {
    return defaultValueQuery.data?.indirectEmissions
      ? Number(defaultValueQuery.data.indirectEmissions)
      : 0
  }, [defaultValueQuery.data])

  const resetFormData = useCallback(() => {
    setFormData(initialState)
  }, [])

  useEffect(() => {
    resetFormData()
  }, [resetFormData])

  const validationResult = useCallback(() => {
    return validateCbamSimulationGoodForm(formData)
  }, [formData])

  const selectedSupplierEmissionsReport = useMemo(() => {
    return supplierEmissionsReports.find(report => report.id === formData.supplierEmissionsReportId)
  }, [supplierEmissionsReports, formData.supplierEmissionsReportId])

  const onClose = useCallback(() => {
    resetFormData()
    close()
  }, [close, resetFormData])

  const onSave = useCallback(async () => {
    const validation = validationResult()
    if (!validation.success) {
      return
    }
    trackCbamSimulationSignal('simulations:good:create:submitted')
    try {
      await createSimulationGood({
        cnCode: formData.cnCode,
        country: formData.country,
        description: formData.description ?? undefined,
        netMassInTonnes: formData.netMassInTonnes,
        isUsingDefaultEmissions: formData.isUsingDefaultEmissions,
        importDate: formData.importDate,
        quarter: formData.quarter ?? undefined,
        supplierEmissionsReportId: formData.supplierEmissionsReportId ?? undefined,
        defaultSpecificDirectEmbeddedEmissions,
        defaultSpecificIndirectEmbeddedEmissions
      })
      trackCbamSimulationSignal('simulations:good:create:succeeded')
      toast.success(
        t(
          'cbamSimulations:cbamSimulationDetails.dialogs.createSimulationGood.notifications.success'
        )
      )
      onClose()
    } catch (error) {
      console.error(error)
      trackCbamSimulationSignal('simulations:good:create:failed')
      toast.error(
        t('cbamSimulations:cbamSimulationDetails.dialogs.createSimulationGood.notifications.error')
      )
    }
  }, [
    validationResult,
    createSimulationGood,
    t,
    formData,
    onClose,
    defaultSpecificDirectEmbeddedEmissions,
    defaultSpecificIndirectEmbeddedEmissions
  ])

  return (
    <GenericDialog
      isOpen={isOpen}
      close={onClose}
      title={t('cbamSimulations:cbamSimulationDetails.dialogs.createSimulationGood.title')}
      size="2xl"
    >
      <div className="space-y-4">
        <CbamSimulationGoodForm
          data={formData}
          onChange={setFormData}
          supplierEmissionsReports={supplierEmissionsReports}
          installations={installations}
          simulationYear={simulationYear}
        />
        <CbamSimulationGoodDataSection
          installationCountry={formData.country}
          simulationYear={simulationYear}
          supplierEmissionsReportId={formData.supplierEmissionsReportId}
          selectedSupplierEmissionsReport={selectedSupplierEmissionsReport}
          defaultSpecificDirectEmbeddedEmissions={defaultSpecificDirectEmbeddedEmissions}
          defaultSpecificIndirectEmbeddedEmissions={defaultSpecificIndirectEmbeddedEmissions}
        />
        <div className="flex justify-end gap-2">
          <Button variant="neutral" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            disabled={!validationResult().success}
            isLoading={isCreatingSimulationGood}
            variant="default"
            onClick={onSave}
          >
            {t('common:actions.save')}
          </Button>
        </div>
      </div>
    </GenericDialog>
  )
}
